import type { FC } from 'react';
import { useMemo, useState, Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { ThemeProvider } from 'styled-components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import * as employeeDetailsSelectors from '@makemydeal/dr-dash-store/selectors/employeeDetailsSelectors';
import * as employeeDetailsActionCreators from '@makemydeal/dr-dash-store/actions/employeeDetailsActionCreators';
import * as employeeDetailsUtils from '@makemydeal/dr-dash-store/utils/employeeDetailsUtils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { AutocompleteOptions } from '@makemydeal/dr-shared-types';
import type { EmployeeDetailsSupportedRoles, EmployeeTeam } from '@makemydeal/dr-dash-types';

import { Button } from '@interstate/components/Button';
import { SlideOut } from '@interstate/components/SlideOut';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';
import { Autocomplete } from '@makemydeal/dr-shared-components';
import {
    EmployeeInputGroup,
    EmployeeButtonGroup,
    SlideOutIntroText,
    SlideOutPanelBody,
    SlideOutScrollableContent,
    SlideOutPinnedFooter
} from './EmployeeDetailSlideOut.style';
import { dealLockHooks } from '../hooks';
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';
import { useEmployeeRoleHandlers, validators, invalidField, populatedField, type InvalidFields } from './useEmployeeRoleHandlers';
import { EmployeeRoleFields } from './EmployeeRoleFields';
import { AdditionalRolesSection } from './AdditionalRolesSection';
import {
    additionalRoleTitleMap,
    alwaysVisibleRoleTitleMap,
    getAdditionalRolesWithData,
    legacyRoleTitleMap
} from './employeeRoleUtils';

const legacyRoleTitleMapOrdered = Object.entries(legacyRoleTitleMap);
const alwaysVisibleRolesOrdered = Object.entries(alwaysVisibleRoleTitleMap);

const INTRO_SENTENCE =
    "Below, you'll find a form for entering team member details. Fill in each field accurately so the necessary associates are added to the deal.";

const LEGACY_INTRO_SENTENCE =
    "Below, you'll find a form dedicated to entering team member details. Please ensure to fill in each field accurately to help ensure that necessary associates are added to the deal.";

const validateEmployees = (
    employeeDetails: EmployeeTeam,
    rolesToValidate: EmployeeDetailsSupportedRoles[],
    allowAnyDmsIdContent: boolean
): InvalidFields => {
    const updatedEmployeeDetails = { ...employeeDetails };
    const invalidFields: Record<string, boolean> = {};

    for (const role of rolesToValidate) {
        const employee = employeeDetails[role as EmployeeDetailsSupportedRoles];
        if (employee) {
            const employeeInvalidFields: Record<string, boolean> = {};
            const employeeInvalidOptionalFields: Record<string, boolean> = {};
            let populatedOptionalFieldCount = 0;

            if (!validators.firstName(employee.name.firstName)) employeeInvalidFields[`${role}-firstName`] = true;
            if (!validators.lastName(employee.name.lastName)) employeeInvalidFields[`${role}-lastName`] = true;
            // prettier-ignore
            switch (validators.employeeDmsId(employee.employeeDmsId, allowAnyDmsIdContent)) {
                case invalidField: employeeInvalidOptionalFields[`${role}-employeeDmsId`] = true; break;
                case populatedField: populatedOptionalFieldCount++; break;
            }

            const totalRequiredEmployeeFields = 2;
            const invalidRequiredFieldCount = Object.keys(employeeInvalidFields).length;
            const invalidOptionalFieldCount = Object.keys(employeeInvalidOptionalFields).length;
            const totalInvalidFieldCount = invalidRequiredFieldCount + invalidOptionalFieldCount;

            if (
                invalidRequiredFieldCount === totalRequiredEmployeeFields &&
                invalidOptionalFieldCount === 0 &&
                populatedOptionalFieldCount === 0
            ) {
                updatedEmployeeDetails[role as EmployeeDetailsSupportedRoles] = undefined;
            } else if (totalInvalidFieldCount > 0) {
                Object.assign(invalidFields, { ...employeeInvalidFields, ...employeeInvalidOptionalFields });
            } else {
                updatedEmployeeDetails[role as EmployeeDetailsSupportedRoles] =
                    employeeDetailsUtils.normalizeEmployeeDetails(employee);
            }
        }
    }

    return {
        employeeDetails: updatedEmployeeDetails,
        fields: invalidFields,
        roles: new Set(Object.keys(invalidFields).map((key) => key.slice(0, key.lastIndexOf('-'))))
    };
};

export const EmployeeDetailSlideOut: FC = () => {
    const dispatch = useDispatch();
    const reduxEmployeeDetails = useSelector(employeeDetailsSelectors.getEmployeeDetails);
    const reduxEmployeeDetailsCloned = useMemo(() => structuredClone(reduxEmployeeDetails), [reduxEmployeeDetails]);
    const [employeeDetails, setEmployeeDetails] = useState(reduxEmployeeDetailsCloned);
    const [invalidFields, setInvalidFields] = useState<InvalidFields>({ fields: {}, roles: new Set() });
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const isSlideOutOpen = useSelector(employeeDetailsSelectors.getSlideOutOpen);
    const isSendingUpdate = useSelector(employeeDetailsSelectors.getSendingUpdate);
    const isSmallScreen = useMediaQuery({ query: `(max-width: ${BreakPoint.SMALL})` });
    const isLocked = dealLockHooks.useIsDealLocked();
    const interstateTheme = useInterstateTheme();

    const isDealerEmployeeSearchEnabled = useSelector(featureToggleSelectors.enableDealerEmployeeSearch);
    const isAdditionalSalesRolesEnabled = useSelector(featureToggleSelectors.isAdditionalSalesRolesEnabled);

    const panelWidth = isSmallScreen ? '100%' : isAdditionalSalesRolesEnabled ? '768px' : '584px';

    const [additionalRolesOnDeal, setAdditionalRolesOnDeal] = useState<EmployeeDetailsSupportedRoles[]>([]);
    const [isSectionExpanded, setIsSectionExpanded] = useState(true);

    const handlers = useEmployeeRoleHandlers({
        employeeDetails,
        setEmployeeDetails,
        invalidFields,
        setInvalidFields
    });

    const { employeeSearchResults, loadingFields, currentSearchKey, clearCounters } = handlers;
    const selectOnFocusSelector = featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap;

    useEffect(() => {
        if (isSlideOutOpen) {
            setEmployeeDetails(structuredClone(reduxEmployeeDetails));
            setInvalidFields({ fields: {}, roles: new Set() });
            setAdditionalRolesOnDeal(getAdditionalRolesWithData(reduxEmployeeDetails));
            setIsSectionExpanded(true);
        }
    }, [isSlideOutOpen, reduxEmployeeDetails]);

    const closeSlideout = () => {
        setEmployeeDetails(structuredClone(reduxEmployeeDetails));
        setInvalidFields({ fields: {}, roles: new Set() });
        dispatch(employeeDetailsActionCreators.closeEmployeeDetailsSlideout());
    };

    const renderedRoles = isAdditionalSalesRolesEnabled
        ? ([...Object.keys(alwaysVisibleRoleTitleMap), ...additionalRolesOnDeal] as EmployeeDetailsSupportedRoles[])
        : (Object.keys(legacyRoleTitleMap) as EmployeeDetailsSupportedRoles[]);

    const rolesToDiff = isAdditionalSalesRolesEnabled
        ? {
              ...alwaysVisibleRoleTitleMap,
              ...Object.fromEntries(additionalRolesOnDeal.map((role) => [role, additionalRoleTitleMap[role as string]]))
          }
        : legacyRoleTitleMap;

    const cancel = () => {
        const unsavedChanges = employeeDetailsUtils.hasUnsavedEmployeeChanges(employeeDetails, reduxEmployeeDetails, rolesToDiff);
        if (unsavedChanges) {
            setShowDiscardModal(true);
        } else {
            closeSlideout();
        }
    };

    const handleDiscardChanges = () => {
        setShowDiscardModal(false);
        closeSlideout();
    };

    const handleGoBack = () => {
        setShowDiscardModal(false);
    };

    const submit = () => {
        const validationResult = validateEmployees(employeeDetails, renderedRoles, isAdditionalSalesRolesEnabled);
        if (validationResult.roles.size > 0) {
            setInvalidFields(validationResult);
            if (additionalRolesOnDeal.some((role) => validationResult.roles.has(role as string))) {
                setIsSectionExpanded(true);
            }
            return;
        }

        const employees = structuredClone(validationResult.employeeDetails!);
        setInvalidFields({ fields: {}, roles: new Set() });
        dispatch(employeeDetailsActionCreators.startSendEmployeeUpdate(employees));
    };

    const footerButtons = (
        <>
            <Button
                buttonStyle={isAdditionalSalesRolesEnabled ? 'secondary' : 'tertiary'}
                size="medium"
                onClick={cancel}
                data-testid="employee-details-slideout-close-button"
            >
                Close
            </Button>
            <Button
                buttonStyle="primary"
                size="medium"
                onClick={submit}
                isLoading={isSendingUpdate}
                data-testid="employee-details-slideout-submit-button"
                disabled={isLocked}
            >
                Update
            </Button>
        </>
    );

    const roleContent = (
        <>
            {isAdditionalSalesRolesEnabled ? (
                <SlideOutIntroText data-testid="employee-details-slideout-description">{INTRO_SENTENCE}</SlideOutIntroText>
            ) : (
                <p data-testid="employee-details-slideout-description">{LEGACY_INTRO_SENTENCE}</p>
            )}
            {isAdditionalSalesRolesEnabled ? (
                <>
                    {alwaysVisibleRolesOrdered.map(([role, title], index) => (
                        <EmployeeRoleFields
                            key={role}
                            role={role as EmployeeDetailsSupportedRoles}
                            title={title}
                            employee={employeeDetails[role as EmployeeDetailsSupportedRoles]}
                            invalidFields={invalidFields.fields}
                            hasRoleError={invalidFields.roles.has(role)}
                            showTrailingDivider={index < alwaysVisibleRolesOrdered.length - 1}
                            handlers={handlers}
                        />
                    ))}
                    {additionalRolesOnDeal.length > 0 && (
                        <AdditionalRolesSection
                            roles={additionalRolesOnDeal}
                            isExpanded={isSectionExpanded}
                            onToggle={() => setIsSectionExpanded((prev) => !prev)}
                            employeeDetails={employeeDetails}
                            invalidFields={invalidFields}
                            handlers={handlers}
                        />
                    )}
                </>
            ) : (
                legacyRoleTitleMapOrdered.map(([role, title]) => (
                    <Fragment key={role as string}>
                        {invalidFields.roles.has(role as string) && (
                            <Alert type="caution" title="Incomplete Form" data-testid={`incomplete-form-warning-${role as string}`}>
                                Please complete all required fields for the team member role to save your information.
                            </Alert>
                        )}
                        <div style={{ marginTop: '3rem' }}>
                            <Typography variant="body-md">{title}</Typography>
                            <EmployeeInputGroup>
                                {isDealerEmployeeSearchEnabled ? (
                                    <Autocomplete
                                        key={`${role}-firstName-reset-${clearCounters[role] || 0}`}
                                        name="firstName"
                                        label="First Name"
                                        hasError={invalidFields.fields[`${role}-firstName`]}
                                        menuOptions={employeeSearchResults[`${role}-firstName`] || []}
                                        onSearch={handlers.createSearchHandler(role, 'firstName')}
                                        onChange={handlers.createChangeHandler(role, 'firstName')}
                                        onSelect={(selectedOption: AutocompleteOptions) =>
                                            handlers.handleEmployeeSelect(role as EmployeeDetailsSupportedRoles, selectedOption)
                                        }
                                        isLoading={
                                            (loadingFields[`${role}-firstName`] || false) &&
                                            currentSearchKey === `${role}-firstName`
                                        }
                                        value={employeeDetails[role as EmployeeDetailsSupportedRoles]?.name.firstName || ''}
                                        data-testid={`employee-first-name-${role}`}
                                        size="small"
                                        selectOnFocusSelector={selectOnFocusSelector}
                                    />
                                ) : (
                                    <TextInput
                                        name="firstName"
                                        label="First Name"
                                        hasError={invalidFields.fields[`${role}-firstName`]}
                                        onChange={(evt) =>
                                            handlers.handleFieldChange(
                                                role as EmployeeDetailsSupportedRoles,
                                                'firstName',
                                                evt.target.value
                                            )
                                        }
                                        onBlur={(evt) =>
                                            handlers.handleFieldBlur(
                                                role as EmployeeDetailsSupportedRoles,
                                                'firstName',
                                                evt.target.value
                                            )
                                        }
                                        value={employeeDetails[role as EmployeeDetailsSupportedRoles]?.name.firstName || ''}
                                        data-testid={`employee-first-name-${role}`}
                                        disabled={isLocked}
                                        size="small"
                                        selectOnFocusSelector={selectOnFocusSelector}
                                    />
                                )}

                                {isDealerEmployeeSearchEnabled ? (
                                    <Autocomplete
                                        key={`${role}-lastName-reset-${clearCounters[role] || 0}`}
                                        name="lastName"
                                        label="Last Name"
                                        hasError={invalidFields.fields[`${role}-lastName`]}
                                        menuOptions={employeeSearchResults[`${role}-lastName`] || []}
                                        onSearch={handlers.createSearchHandler(role, 'lastName')}
                                        onChange={handlers.createChangeHandler(role, 'lastName')}
                                        onSelect={(selectedOption: AutocompleteOptions) =>
                                            handlers.handleEmployeeSelect(role as EmployeeDetailsSupportedRoles, selectedOption)
                                        }
                                        isLoading={
                                            (loadingFields[`${role}-lastName`] || false) && currentSearchKey === `${role}-lastName`
                                        }
                                        value={employeeDetails[role as EmployeeDetailsSupportedRoles]?.name.lastName || ''}
                                        data-testid={`employee-last-name-${role}`}
                                        size="small"
                                        selectOnFocusSelector={selectOnFocusSelector}
                                    />
                                ) : (
                                    <TextInput
                                        name="lastName"
                                        label="Last Name"
                                        hasError={invalidFields.fields[`${role}-lastName`]}
                                        onChange={(evt) =>
                                            handlers.handleFieldChange(
                                                role as EmployeeDetailsSupportedRoles,
                                                'lastName',
                                                evt.target.value
                                            )
                                        }
                                        onBlur={(evt) =>
                                            handlers.handleFieldBlur(
                                                role as EmployeeDetailsSupportedRoles,
                                                'lastName',
                                                evt.target.value
                                            )
                                        }
                                        value={employeeDetails[role as EmployeeDetailsSupportedRoles]?.name.lastName || ''}
                                        data-testid={`employee-last-name-${role}`}
                                        disabled={isLocked}
                                        size="small"
                                        selectOnFocusSelector={selectOnFocusSelector}
                                    />
                                )}
                            </EmployeeInputGroup>

                            <EmployeeInputGroup>
                                <TextInput
                                    name="dmsId"
                                    label="DMS ID (optional)"
                                    hasError={invalidFields.fields[`${role}-employeeDmsId`]}
                                    onChange={(evt) =>
                                        handlers.handleFieldChange(
                                            role as EmployeeDetailsSupportedRoles,
                                            'employeeDmsId',
                                            evt.target.value
                                        )
                                    }
                                    onBlur={(evt) =>
                                        handlers.handleFieldBlur(
                                            role as EmployeeDetailsSupportedRoles,
                                            'employeeDmsId',
                                            evt.target.value
                                        )
                                    }
                                    value={employeeDetails[role as EmployeeDetailsSupportedRoles]?.employeeDmsId || ''}
                                    data-testid={`employee-dms-id-${role}`}
                                    disabled={isLocked}
                                    size="small"
                                    selectOnFocusSelector={selectOnFocusSelector}
                                />
                            </EmployeeInputGroup>

                            <EmployeeButtonGroup>
                                <Button
                                    buttonStyle="tertiary"
                                    size="small"
                                    data-testid={`employee-clear-button-${role}`}
                                    disabled={isLocked}
                                    onClick={() => handlers.handleClear(role as EmployeeDetailsSupportedRoles)}
                                >
                                    Clear
                                </Button>
                            </EmployeeButtonGroup>
                            <hr style={{ marginTop: '1rem' }} />
                        </div>
                    </Fragment>
                ))
            )}
        </>
    );

    return (
        <SlideOut
            header="Sales Team"
            show={isSlideOutOpen}
            panelWidth={panelWidth}
            position="right"
            onHide={cancel}
            data-testid="employee-details-slideout"
            sx={isAdditionalSalesRolesEnabled ? { '.ids-SlideOut-content': { height: '100%', overflowY: 'hidden' } } : undefined}
        >
            {isAdditionalSalesRolesEnabled ? (
                <ThemeProvider theme={interstateTheme}>
                    <SlideOutPanelBody>
                        <SlideOutScrollableContent>{roleContent}</SlideOutScrollableContent>
                        <SlideOutPinnedFooter>{footerButtons}</SlideOutPinnedFooter>
                    </SlideOutPanelBody>
                </ThemeProvider>
            ) : (
                <>
                    {roleContent}
                    <EmployeeButtonGroup>{footerButtons}</EmployeeButtonGroup>
                </>
            )}
            <DiscardChangesModal
                show={showDiscardModal}
                onDiscard={handleDiscardChanges}
                onGoBack={handleGoBack}
                hasValidationErrors={invalidFields.roles.size > 0}
            />
        </SlideOut>
    );
};
